import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { scheduleService } from '../services/scheduleService';

interface VenueOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  venueName: string;
  currentAddress?: string;
  onSaved: () => void;
}

export const VenueOverrideDialog: React.FC<VenueOverrideDialogProps> = ({
  open,
  onClose,
  venueName,
  currentAddress,
  onSaved,
}) => {
  const [address, setAddress] = useState(currentAddress || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!address.trim()) return;

    setSaving(true);
    setError(null);

    try {
      await scheduleService.setVenueOverride(venueName, address.trim());
      setAddress('');
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setAddress('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{currentAddress ? 'Edit Venue Address' : 'Set Venue Address'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Venue
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {venueName}
          </Typography>
        </Box>
        <TextField
          autoFocus
          fullWidth
          label="Address"
          placeholder="e.g., 1901 Oakesdale Ave SW, Renton, WA 98057"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && address.trim()) {
              handleSave();
            }
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !address.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
